import React, { useState } from "react";
import { Transition } from "@headlessui/react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggleFAQ = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  const toggleAnswer = () => {
    setIsOpen(!isOpen);
  };

  const faqs = [
    {
      title: "What is the UK eVisa or Electronic Visa Waiver (EVW)?",
      content: (
        <p>
          The UK eVisa or Electronic Visa Waiver (EVW) is a digital travel
          authorization granted to citizens of select countries such as the UAE,
          Kuwait, Qatar and Oman. It allows travelers to visit the UK without
          requiring a traditional visa. This simplified online application
          process makes it easier for eligible individuals to travel.
        </p>
      ),
    },
    {
      title: "How do I apply for the UK eVisa?",
      content: (
        <p>
          To apply for the UK eVisa, you will need a valid passport from an
          eligible country, a credit or debit card for payment and internet
          access to complete the online application. Once your application is
          approved, you will receive a link to download your EVW.
        </p>
      ),
    },
    {
      title: "What is the validity period of the UK eVisa?",
      content: (
        <p>
          The UK eVisa remains valid for up to 180 days from the date it is
          issued.
        </p>
      ),
    },
    {
      title: "Can I pay the visa fee in AED?",
      content: (
        <p>
          The visa fee must be paid in US dollars, regardless of your location
          in the Gulf.
        </p>
      ),
    },
    {
      title: "When should I apply for the UK eVisa?",
      content: (
        <p>
          It is advisable to submit your application up to three months before
          your planned travel date.
        </p>
      ),
    },
    {
      title: "Can a non-Gulf citizen apply for a UK visa from the Gulf?",
      content: (
        <p>
          Yes, individuals who are not citizens of the Gulf countries but are
          legally residing in one of the Gulf states can apply for a UK visa
          from there.
        </p>
      ),
    },
    {
      title: "Is there an expedited processing option for UK visas?",
      content: (
        <p>
          Yes, expedited visa processing is available for an additional fee.
          This service allows your visa application to be processed in as little
          as 2-3 business days.
        </p>
      ),
    },
    {
      title: "What should I do if I miss my visa appointment?",
      content: (
        <p>
          If you miss your visa appointment, you must reschedule by cancelling
          the current appointment and selecting a new date through the online
          portal.
        </p>
      ),
    },
    {
      title: "How long does it take to process a UK visa in Dubai?",
      content: (
        <p>
          For non-settlement visas, processing typically takes 10-15 business
          days. Priority visa services can reduce this time to 3-5 days.
          Settlement visas (such as spouse visas) can take up to 60 days, or 30
          days with priority processing.
        </p>
      ),
    },
    {
      title:
        "What documents are needed to apply for a UK visitor visa from the UAE?",
      content: (
        <ul className="list-inside list-disc">
          <li>Printed and completed UK visa online application form.</li>
          <li>A valid passport with adequate validity.</li>
          <li>Previous passports, if applicable.</li>
          <li>Translation of any documents not in English or Welsh.</li>
          <li>A valid UAE residence visa (for non-UAE nationals).</li>
          <li>An invitation letter (if applicable).</li>
          <li>Proof of employment or business registration.</li>
          <li>Recent bank statements showing sufficient funds.</li>
          <li>Tax registration and tax returns.</li>
          <li>Accommodation details in the UK.</li>
          <li>Marriage certificate (if applicable).</li>
        </ul>
      ),
    },
    {
      title: "What is the climate like in the UK?",
      content: (
        <p>
          The UK experiences a variable climate with four seasons:
          <ul className="mt-2 list-inside list-disc">
            <li>
              <strong>Winter (Dec-Feb):</strong> Cold with potential snowfall.
            </li>
            <li>
              <strong>Spring (Mar-May):</strong> Temperate with flowers
              blooming.
            </li>
            <li>
              <strong>Summer (Jun-Aug):</strong> Mild and sunny.
            </li>
            <li>
              <strong>Autumn (Sep-Nov):</strong> Crisp weather with falling
              leaves.
            </li>
          </ul>
        </p>
      ),
    },
    {
      title: "What are the four countries in the UK and their capitals?",
      content: (
        <ul className="list-inside list-disc">
          <li>England (London)</li>
          <li>Scotland (Edinburgh)</li>
          <li>Wales (Cardiff)</li>
          <li>Northern Ireland (Belfast)</li>
        </ul>
      ),
    },
    {
      title:
        "Can I apply for a UK visa with a passport that is over 10 years old?",
      content: (
        <p>
          No, your passport must be less than 10 years old to apply for a UK
          visa. If your passport has expired or is older, you will need to renew
          it before submitting your application.
        </p>
      ),
    },
    {
      title: "Can I retrieve my passport during the visa processing?",
      content: (
        <p>
          In most cases, passports cannot be retrieved once the visa application
          is in process. However, there may be exceptions for certain
          professionals (e.g., pilots) or under special circumstances approved
          by the embassy.
        </p>
      ),
    },
    {
      title: "Is travel insurance required for a UK visa?",
      content: (
        <p>
          Yes, travel insurance is a mandatory requirement for obtaining a UK
          visa. It covers potential emergencies such as medical treatment,
          accidents, or unforeseen events during your stay.
        </p>
      ),
    },
    {
      title: "What is a Schengen visa?",
      content: (
        <p>
          A Schengen visa allows travel to 26 European countries that are part
          of the Schengen Area, which has removed internal border controls.
          However, the Schengen visa does not permit entry into the UK.
        </p>
      ),
    },
    {
      title: "Can I visit Schengen countries with my UK visa?",
      content: (
        <p>No, a UK visa does not grant entry into Schengen countries.</p>
      ),
    },
    {
      title: "What are the Schengen visa requirements for minors?",
      content: (
        <ul className="list-inside list-disc">
          <li>Birth certificate of the minor.</li>
          <li>Completed application forms signed by both parents.</li>
          <li>Family court order if only one parent has custody.</li>
          <li>Certified copies of parents’ identification documents.</li>
          <li>Parental consent if traveling alone.</li>
        </ul>
      ),
    },
    {
      title:
        "What are the Schengen visa requirements for different employment statuses?",
      content: (
        <ul className="list-inside list-disc">
          <li>
            <strong>Employees:</strong> Employment contract, 6 months' bank
            statements, leave approval and tax returns.
          </li>
          <li>
            <strong>Self-employed individuals:</strong> Business license, 6
            months' company bank statements and tax returns.
          </li>
          <li>
            <strong>Students:</strong> Acceptance letter, no-objection
            certificate and academic records.
          </li>
          <li>
            <strong>Retirees:</strong> 6 months' pension statements.
          </li>
        </ul>
      ),
    },
  ];

  return (
    <>
      <div className="flex w-full flex-col items-center justify-center py-20">
        {isOpen && (
          <div className="mb-6 w-3/4 text-black rounded-md bg-gray-100 p-4 text-lg shadow-md">
            <p>
              - Flexible immigration policies and a high standard of living
              attract skilled professionals worldwide. <br />
              - Home to globally recognized universities like Oxford, Cambridge,
              and Imperial College London. <br />
              - Offers free healthcare and education, ensuring a safe and
              comfortable lifestyle. <br />
              - A booming economy with low unemployment rates and excellent
              career opportunities in various sectors. <br />
              - A rich cultural heritage, vibrant cities and scenic countryside
              provide a blend of modernity and tradition. <br />
              - Transparent pathways to citizenship with long-term benefits and
              settled status. <br />- Programs like post-study visas and
              apprenticeships support skilled newcomers and students.
            </p>
          </div>
        )}
        <button
          onClick={toggleAnswer}
          className="flex md:w-1/2 w-2/2 items-center justify-center rounded-full bg-blue-600 px-6 py-3 text-center text-md font-semibold text-white transition duration-300 ease-in-out hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        >
          Why Choose the UK ?
          <svg
            className={`ml-3 h-4 w-4 transform transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 5v14M5 12l7 7 7-7"></path>
          </svg>
        </button>
      </div>

      <div className="mx-auto max-w-5xl rounded-lg bg-white p-6">
        <h2 className="mb-4 text-center text-4xl font-semibold text-[#011473]">
          FAQ's
        </h2>
      </div>

      <div className="mx-auto max-w-5xl p-6">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="mb-4 overflow-hidden rounded-lg border shadow-sm"
          >
            <button
              className={`flex w-full items-center justify-between px-4 py-3 text-left font-semibold ${
                openIndex === index ? "bg-blue-600 text-white" : "bg-gray-100"
              }`}
              onClick={() => toggleFAQ(index)}
            >
              {faq.title}
              <span
                className={`transform transition-transform ${
                  openIndex === index ? "rotate-180" : "rotate-0"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </button>
            <Transition
              show={openIndex === index}
              enter="transition-all duration-200 ease-out"
              enterFrom="max-h-0 opacity-0"
              enterTo="max-h-96 opacity-100"
              leave="transition-all duration-200 ease-in"
              leaveFrom="max-h-96 opacity-100"
              leaveTo="max-h-0 opacity-0"
            >
              <div className="bg-white px-4 py-3 text-gray-700">
                {faq.content}
              </div>
            </Transition>
          </div>
        ))}
      </div>
    </>
  );
};

export default FAQ;
